import { MenuItem } from 'primeng/api';

interface ExtendedMenuItem extends MenuItem {
	permission?: string;
	badgeCount?: number;
	/** Set false to opt an item out of the favorites star. */
	favoritable?: boolean;
	items?: ExtendedMenuItem[];
}

export type Role =
	| 'Admin'
	| 'patient_manager_1'
	| 'patient_manager_2'
	| 'patient_manager_3'
	| 'dispatcher_1'
	| 'dispatcher_2'
	| 'item_manager_1'
	| 'item_manager_2'
	| 'item_manager_3'
	| 'order_manager_1'
	| 'order_manager_2'
	| 'order_manager_3'
	| 'driver'
	| 'vehicle_manager_1'
	| 'vehicle_manager_2'
	| 'vehicle_manager_3';

export const SIDEBAR_MENU_ITEMS: ExtendedMenuItem[] = [
	{
		label: 'My Favorites',
		permission: 'company-portal.menu.my-favorites',
		// items are populated dynamically at runtime by AppMenuComponent from FavoritesService
		items: []
	},
	/**
	 * TODO: remove driver module
	 */
	// {
	// 	label: 'Driver',
	// 	items: [
	// 		{ label: 'Loading Truck', routerLink: ['driver/loading-truck'] },
	// 		{ label: 'My Orders', routerLink: ['driver/driver-orders'] }
	// 	]
	// },
	{
		label: 'Admin',
		permission: 'company-portal.menu.admin',
		items: [
			{ label: 'User', routerLink: ['admin/users/search'], permission: 'company-portal.menu.admin.user' },
			{
				label: 'System Settings',
				permission: 'company-portal.menu.admin.system-settings',
				items: [
					{ label: 'Item Category', routerLink: ['admin/categories'] },
					{ label: 'Rx Configuration', routerLink: ['admin/rx-configuration'] }
				]
			}
		]
	},
	{
		label: 'Billing',
		permission: 'company-portal.menu.billing',
		items: [
			{
				label: 'Lost Items',
				routerLink: ['billing/lost-items'],
				permission: 'company-portal.menu.billing.lost-items'
			},
			{ label: 'Invoice', routerLink: ['billing/invoice'], permission: 'company-portal.menu.billing.invoice' },
			{
				label: 'Quick Add Patient',
				routerLink: ['billing/quick-add-patient'],
				permission: 'company-portal.menu.billing.quick-add-patient'
			}
		]
	},
	{
		// These route into the `admin` module (/admin/…), not a "company" one —
		// see the section→route map in admin.routing.module.ts. "Vehicle" is the
		// "truck" feature (admin/trucks).
		label: 'Company',
		permission: 'company-portal.menu.company',
		items: [
			{ label: 'Branch', routerLink: ['admin/branches'], permission: 'branch.search' },
			{ label: 'Branch Zipcodes', routerLink: ['admin/branch-zipcodes'] },
			{ label: 'Vehicle', routerLink: ['admin/trucks'] }
		]
	},
	{
		label: 'Inbound Processing',
		permission: 'company-portal.menu.inbound-processing',
		items: [
			{ label: 'Order Intake', routerLink: ['inbound-processing'], badgeCount: 0 },
			{ label: 'Communications', routerLink: ['inbound-communication'], badgeCount: 0 },
			{
				label: 'Data Management',
				routerLink: ['data-management'],
				permission: 'inbound-order-processing.edit',
				badgeCount: 0
			}
		]
	},
	{
		label: 'Insurance',
		permission: 'company-portal.menu.insurance',
		items: [
			{ label: 'Insurance', routerLink: ['admin/insurance'] },
			{
				label: 'Global Insurance',
				routerLink: ['admin/global-insurance'],
				permission: 'company-portal.menu.insurance.global-insurance'
			},
			{ label: 'Rate Template', routerLink: ['admin/insurance-rate-template'] }
		]
	},
	{
		label: 'Inventory',
		permission: 'company-portal.menu.inventory',
		items: [
			{ label: 'Asset', routerLink: ['inventory/asset'] },
			{ label: 'Cleaning Log', routerLink: ['inventory/cleaning-log'] },
			{ label: 'Inventory Map', routerLink: ['inventory/map'] },
			{ label: 'Item', routerLink: ['inventory/search-item'] },
			{ label: 'Kit', routerLink: ['inventory/kits'], permission: 'kit.search' },
			{ label: 'Maintenance Log', routerLink: ['inventory/maintenance-log'] },
			{ label: 'Movement History', routerLink: ['inventory/movement-history'] },
			{ label: 'Oxygen Log', routerLink: ['inventory/oxygen-log'] },
			{ label: 'Physical Inventory', routerLink: ['inventory/physical-inventory'] },
			{ label: 'Purchase Order', routerLink: ['purchase-order'] },
			{ label: 'Repair Log', routerLink: ['inventory/repair-log'] },
			{ label: 'Transfer Order', routerLink: ['inventory/transfer-order'] },
			{ label: 'Vendor', routerLink: ['admin/vendors'] }
		]
	},
	{
		label: 'Order',
		permission: 'company-portal.menu.order',
		items: [
			{ label: 'Dispatcher', routerLink: ['customer-orders/dispatcher'] },
			{ label: 'Order Map', routerLink: ['customer-orders/map'] },
			{ label: 'Search Orders', routerLink: ['customer-orders/order-list'] }
		]
	},
	{
		label: 'Patient',
		permission: 'company-portal.menu.patient',
		items: [
			{ label: 'Add Patient', routerLink: ['patients/new'] },
			{ label: 'Facility', routerLink: ['patients/facility'] },
			{ label: 'Patient Map', routerLink: ['patients/map'] },
			{ label: 'Prescription (Rx)', routerLink: ['patients/rx'] },
			{ label: 'Search Patients', routerLink: ['patients'] }
		]
	},
	{
		label: 'Reporting',
		permission: 'company-portal.menu.reporting',
		items: [
			{
				label: 'Company Reports',
				icon: 'pi pi-receipt',
				routerLink: ['reports/company'],
				permission: 'company-report.view'
			}
		]
	},
	{
		label: 'Tools',
		permission: 'company-portal.menu.tools',
		items: [
			{ label: 'AMPS Fix', routerLink: ['amps-fix'], permission: 'company-portal.menu.tools.amps-fix' },
			{ label: 'Asset Owner', routerLink: ['asset-owner'], permission: 'company-portal.menu.tools.asset-owner' },
			{
				label: 'Asset Branch Transfer',
				routerLink: ['asset-branch-transfer'],
				permission: 'company-portal.menu.tools.asset-branch-transfer'
			},
			{
				label: 'Non-Locatable to Located',
				routerLink: ['non-locatable-to-located'],
				permission: 'company-portal.menu.tools.non-locatable-to-located'
			},
			{ label: 'Import Switch-out', routerLink: ['switch-out'] }
		]
	}
];
